import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "../providers/supabase/supabase";

// Payload of the dashboard-api GET /config/i2v-active endpoint (echoflicks-infra):
// which i2v provider+model the composer ACTUALLY runs in this environment, resolved
// from the Batch job-definition revision the video Step Function pins. Reached through
// the session-gated /api/dashboard proxy, so prod CRM shows prod and staging CRM shows
// staging without any client-side switching.

export type ActiveTier = {
  provider_id: string | null;
  model_id: string | null;
  model_source: "env" | "composer_default" | "secret" | null;
  source: string;
  unresolved_reason?: string;
  same_as_primary?: boolean;
};

export type ActiveI2vConfig = {
  environment: string;
  environment_label: string;
  generated_at?: string;
  job_definition: {
    name: string;
    resolved_revision: number;
    latest_active_revision: number | null;
    pinned_by_state_machine: boolean;
    revision_drift: boolean;
    status?: string;
  };
  tiers: { primary: ActiveTier; secondary: ActiveTier };
  raw: Record<string, string>;
  notes: string[];
};

function normalizeActiveI2vConfig(value: unknown): ActiveI2vConfig | null {
  if (typeof value !== "object" || value === null) return null;
  const candidate = value as Record<string, unknown>;
  const tiers = candidate.tiers as Record<string, unknown> | undefined;
  if (
    typeof candidate.environment !== "string" ||
    typeof tiers !== "object" ||
    tiers === null ||
    !("primary" in tiers)
  ) {
    return null;
  }
  const secondary = tiers.secondary ?? tiers.light;
  if (typeof secondary !== "object" || secondary === null) return null;
  return {
    ...(candidate as unknown as ActiveI2vConfig),
    tiers: {
      primary: tiers.primary as ActiveTier,
      secondary: secondary as ActiveTier,
    },
  };
}

async function fetchActiveI2v(): Promise<ActiveI2vConfig> {
  let token: string | undefined;
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    token = data.session?.access_token;
  } catch {
    // No session -> the proxy will 401 and the page renders without badges.
  }
  const response = await fetch("/api/dashboard/config/i2v-active", {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`config/i2v-active responded ${response.status}`);
  }
  const payload: unknown = await response.json();
  const active = normalizeActiveI2vConfig(payload);
  if (!active) {
    throw new Error("config/i2v-active returned an unexpected shape");
  }
  return active;
}

// Active tier config, or null when the endpoint is unavailable (older dashboard-api,
// missing IAM, no session) — the AI Settings page then renders exactly as before.
export function useActiveI2v(): {
  active: ActiveI2vConfig | null;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ["ai-i2v-active"],
    queryFn: fetchActiveI2v,
    staleTime: 60 * 1000,
    retry: false,
  });
  return { active: query.data ?? null, isLoading: query.isLoading };
}

export type TierRole = "primary" | "secondary";

// Which badge(s) a provider card should carry. kling_selector is a pseudo-provider
// (auto-picks kling_direct/kling_piapi at runtime), so it never matches a card and is
// only described in the active-configuration summary.
export function activeRolesForProvider(
  active: ActiveI2vConfig | null,
  providerId: string,
): TierRole[] {
  if (!active) return [];
  const roles: TierRole[] = [];
  if (active.tiers.primary.provider_id === providerId) roles.push("primary");
  if (active.tiers.secondary.provider_id === providerId)
    roles.push("secondary");
  return roles;
}

export function describeTier(tier: ActiveTier): string {
  if (!tier.provider_id) return "unresolved";
  return tier.model_id
    ? `${tier.provider_id} · ${tier.model_id}`
    : tier.provider_id;
}
